import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../../api/api";
import { getFilenameFromContentDisposition } from "../../../helpers";
import { APIError } from "../../../constants/types";
import { ILeadApplication } from "./leadApplications.types";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";

const LEAD_APPLICATIONS_API_URL = "/lead/get-lead-applications";

export const fetchLeadApplicationsList = createAsyncThunk<
  {
    data: ILeadApplication[];
    totalRecords: number;
  },
  {
    lead_id: string;
    pageNumber?: number;
    rowsInPerPage?: number;
  },
  { rejectValue: APIError }
>(
  "leads/fetchLeadApplicationsList",
  async ({ lead_id, pageNumber = 1, rowsInPerPage = 25 }, { rejectWithValue }) => {
    try {
      const url = `${LEAD_APPLICATIONS_API_URL}?lead_id=${lead_id}&pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}`;
      const res = await api.get(url);
      const data: ILeadApplication[] = res.data.data;
      const totalRecords = res.data.totalRecords;
      return { data, totalRecords };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || "Failed to fetch lead applications",
        status: err.response?.status,
      });
    }
  }
);

// Download PDF - uses habitational API for habitational type, otherwise commAuto API
export const downloadLeadApplicationPDF =
  (
    leadId: string,
    customerPolicyId: number,
    incrementalNumber: number,
    lineOfBusiness: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());

      let url: string;
      if (lineOfBusiness === "Habitational") {
        url = `/policy/get-habitational-application-pdf?lead_id=${leadId}&customer_policy_id=${customerPolicyId}&incremental_number=${incrementalNumber}&isLead=true`;
      } else {
        const policyType = lineOfBusiness === "Automobile" ? "automobile" : "commauto";
        url = `/policy/get-commauto-and-automobile-application-v2-pdf?policy=${policyType}&lead_id=${leadId}&customer_policy_id=${customerPolicyId}&incremental_number=${incrementalNumber}&isLead=true`;
      }

      const res = await api.get(url, {
        responseType: "arraybuffer",
      });

      const pdfBuffer = res.data;
      const pdfBlob = new Blob([pdfBuffer], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = getFilenameFromContentDisposition(res);
      link.click();
      URL.revokeObjectURL(pdfUrl);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Download failed",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Convert lead application to policy
export const convertLeadApplicationToPolicy =
  (
    payload: {
      customer_id: string;
      type: string;
      lead_id: string;
      incremental_number: number;
    },
    onSuccess: () => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());
      await api.post("/policy/create-policy-from-lead", payload);
      dispatch(
        showMessage({
          type: "success",
          message: "Policy created successfully",
          displayAs: "snackbar",
        })
      );
      onSuccess();
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to convert to policy",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Preview PDF - uses habitational API for habitational type, otherwise commAuto API
export const previewLeadApplicationPDF =
  (
    leadId: string,
    customerPolicyId: number,
    incrementalNumber: number,
    lineOfBusiness: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(saveLoaderProgress());

      let url: string;
      if (lineOfBusiness === "Habitational") {
        url = `/policy/get-habitational-application-pdf?lead_id=${leadId}&customer_policy_id=${customerPolicyId}&incremental_number=${incrementalNumber}&is_habitational_application_preview=true&isLead=true`;
      } else {
        const policyType = lineOfBusiness === "Automobile" ? "automobile" : "commauto";
        url = `/policy/get-commauto-and-automobile-application-v2-pdf?policy=${policyType}&lead_id=${leadId}&customer_policy_id=${customerPolicyId}&incremental_number=${incrementalNumber}&is_commauto_automobile_application_preview=true&isLead=true`;
      }

      const res = await api.get(url);

      for (const key in res.data.data) {
        const newTab = window.open("", "_blank");
        if (newTab) {
          newTab.document.write(res.data.data[key]);
          newTab.document.close();
        }
      }
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Preview failed",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
